import { useCallback, useEffect, useState } from "react";
import { fetchCompanyProfile, updateCompanyProfile } from "../api/companyApi";
import { enterpriseCompanyProfile } from "../data";
import { getAuthToken } from "../auth";

export function useEnterpriseCompanyProfile() {
  const [name, setName] = useState(enterpriseCompanyProfile.name);
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usingApi, setUsingApi] = useState(false);

  const load = useCallback(async () => {
    if (!getAuthToken()) {
      setUsingApi(false);
      setName(enterpriseCompanyProfile.name);
      setAddress("");
      setPhone("");
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    setUsingApi(true);

    try {
      const profile = await fetchCompanyProfile();
      setName(profile.name);
      setAddress(profile.address ?? "");
      setPhone(profile.phone ?? "");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Impossible de charger le profil.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = useCallback(async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError("Le nom de l'entreprise est obligatoire.");
      return false;
    }

    if (!getAuthToken()) {
      setError(null);
      return true;
    }

    setSaving(true);
    setError(null);
    setUsingApi(true);

    try {
      const profile = await updateCompanyProfile({
        name: trimmedName,
        address: address.trim(),
        phone: phone.trim(),
      });
      setName(profile.name);
      setAddress(profile.address ?? "");
      setPhone(profile.phone ?? "");
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Impossible d'enregistrer le profil.";
      setError(message);
      return false;
    } finally {
      setSaving(false);
    }
  }, [name, address, phone]);

  return {
    name,
    address,
    phone,
    loading,
    saving,
    error,
    usingApi,
    setName,
    setAddress,
    setPhone,
    save,
    reload: load,
  };
}
